import {
  ContributionFormData,
  UseContributionFormProps,
  UseCreateContributionProps,
} from 'app/components/Song/Contributions/ContributionForm/types';
import {
  createContributionEffect,
  deleteSongContributionEffect,
  patchContributionEffect,
} from 'app/store/SongList/effects';
import { addAlertAction } from 'app/store/Alerts/slice';
import { routes } from 'app/routing/routesConfig';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getAffectsSplit } from 'app/store/SongList/selectors';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useForm } from 'react-hook-form';
import {
  getDefaultValues,
  getDirtyFieldsWithAdditionalInfo,
} from 'app/components/Song/Contributions/ContributionForm/utils';
import { getContributionFormValidationSchema } from 'app/components/Song/Contributions/ContributionForm/validation';
import { getDirtyValues } from 'app/utils/utils';

export const useContributionState = ({
  newContributionClass,
  contribution: contributionProp,
}: UseCreateContributionProps) => {
  const dispatch = useAppDispatch();
  const { guid } = useParams();
  const [contribution, setContribution] = useState(contributionProp);
  const affectsSplit = useAppSelector(
    getAffectsSplit(contribution?.className || newContributionClass)
  );

  const createOrUpdateContribution = async (
    data: ContributionFormData,
    dirtyValues: Partial<ContributionFormData>
  ) => {
    const { contributor, ...contributorData } = data;
    const deleteItemKey = contributorData.profileGuid ? 'name' : 'profileGuid';
    delete contributorData[deleteItemKey];

    try {
      if (!contribution) {
        const newContribution = await dispatch(
          createContributionEffect({ formData: contributorData })
        ).unwrap();

        try {
          const { profileGuid, ...newDirtyFields } = dirtyValues;
          const dirtyFieldsWithAdditionalInfo = getDirtyFieldsWithAdditionalInfo(
            newDirtyFields,
            contributorData
          );
          await dispatch(
            patchContributionEffect({
              dirtyValues: dirtyFieldsWithAdditionalInfo,
              contributionGuid: newContribution.guid,
              className: newContribution.className,
              profileGuid,
            })
          ).unwrap();
          if (affectsSplit) {
            dispatch(
              addAlertAction({
                message: 'Credit created and may affect an existing split',
              })
            );
          } else {
            dispatch(addAlertAction({ message: 'Credit created' }));
          }
        } catch (e: any) {
          setContribution(newContribution);

          window.history.replaceState(
            null,
            'Contribution',
            routes.songContribution.path
              .replace(':guid', guid as string)
              .replace(':contributionGuid', newContribution.guid)
          );

          if (e?.errorCode === ApiErrorCodeTypes.INVALID_ISNI) {
            dispatch(
              addAlertAction({
                message: ApiErrorCodeMessages.CREDIT_INVALID_ISNI_MESSAGE,
              })
            );
          }

          throw new Error(
            'Credit created, but failed to save details. Please try again'
          );
        }
      } else {
        try {
          await dispatch(
            patchContributionEffect({
              dirtyValues,
              contributionGuid: contribution.guid,
              className: contribution.className,
            })
          ).unwrap();
          dispatch(addAlertAction({ message: 'Credit updated' }));
        } catch (e: any) {
          let errorMsg;
          if (e?.errorCode === ApiErrorCodeTypes.INVALID_ISNI) {
            errorMsg = ApiErrorCodeMessages.CREDIT_INVALID_ISNI_MESSAGE;
          } else {
            errorMsg = 'Credit update failed. Please try again';
          }

          throw new Error(errorMsg);
        }
      }
    } catch (e: any) {
      dispatch(
        addAlertAction({
          message: e?.message || 'Unexpected error occurred. Please try again',
        })
      );
      throw e;
    }
  };

  const handleContributionDelete = async (contributionGuid: string) => {
    try {
      await dispatch(
        deleteSongContributionEffect({ guid: contributionGuid })
      ).unwrap();
    } catch (e) {
      // proceed
    }
  };

  return { createOrUpdateContribution, handleContributionDelete, contribution };
};

export const useContributionForm = ({
  createValidation,
  patchValidation,
  contribution,
  countries,
  createOrUpdateContribution,
  contributionClassesAndRoles,
  onSuccessfulSubmit,
}: UseContributionFormProps) => {
  const formProps = useForm<ContributionFormData>({
    defaultValues: getDefaultValues({
      contribution,
      countries,
      contributionClassesAndRoles,
    }),
    mode: 'onChange',
    resolver: yupResolver(
      getContributionFormValidationSchema(
        createValidation,
        contribution,
        patchValidation
      )
    ),
  });
  const { handleSubmit, formState, watch } = formProps;
  const { dirtyFields } = formState;
  const contributionClassName = watch('className');
  const watchContributorSearch = watch('contributor');

  const getOnSubmit = async (data: ContributionFormData) => {
    const { contributor, ...contributorData } = data;
    const deleteItemKey = contributorData.profileGuid ? 'name' : 'profileGuid';
    delete contributorData[deleteItemKey];
    const dirtyValues = getDirtyValues<ContributionFormData>(
      dirtyFields,
      contributorData
    );

    if (dirtyFields?.performerRoleDetail && !dirtyFields?.performerRole) {
      dirtyValues.performerRole = data.performerRole;
    }

    await createOrUpdateContribution(data, dirtyValues);
    onSuccessfulSubmit();
  };

  const onValid = (throwError = false) => (data: ContributionFormData) => {
    try {
      getOnSubmit(data);
    } catch (e) {
      if (throwError) {
        throw e;
      }
    }
  };

  const getHandleSubmit = (throwError = false) =>
    handleSubmit(onValid(throwError));

  return {
    ...formProps,
    contributionClassName,
    watchContributorSearch,
    getHandleSubmit,
    getOnSubmit,
  };
};
